"use client";

import Link from "next/link";
import { ChevronDown } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "~/components/ui/drawer";

export interface ContentCategoryNavItem {
  id: string;
  label: string;
  href: string;
  count?: number;
}

interface ContentCategoryNavProps {
  items: ContentCategoryNavItem[];
  selectedId: string;
  ariaLabel: string;
  onNavigate?: (item: ContentCategoryNavItem) => void;
}

export function ContentCategoryNav({
  items,
  selectedId,
  ariaLabel,
  onNavigate,
}: ContentCategoryNavProps) {
  const selectedItem = items.find((item) => item.id === selectedId) ?? items[0];

  if (!selectedItem) return null;

  return (
    <nav aria-label={ariaLabel}>
      <div className="hidden flex-wrap gap-2 md:flex">
        {items.map((item) => {
          const isActive = item.id === selectedId;

          return (
            <Link
              key={item.id}
              href={item.href}
              aria-current={isActive ? "page" : undefined}
              onClick={() => onNavigate?.(item)}
              className={`flex h-8 items-center rounded-lg border px-1 pl-3 text-sm transition-colors ${
                isActive
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:bg-muted"
              }`}
            >
              <span>{item.label}</span>
              {item.count !== undefined && (
                <span
                  className={`ml-2 flex h-6 min-w-6 items-center justify-center rounded-md border text-xs font-medium ${
                    isActive
                      ? "border-border/40 bg-background text-primary"
                      : "border-border"
                  }`}
                >
                  {item.count}
                </span>
              )}
            </Link>
          );
        })}
      </div>

      <Drawer>
        <DrawerTrigger className="border-border hover:bg-muted flex w-full items-center justify-between rounded-lg border px-4 py-2 transition-colors md:hidden">
          <span className="text-sm font-medium">{selectedItem.label}</span>
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </DrawerTrigger>

        <DrawerContent className="md:hidden">
          <DrawerHeader>
            <DrawerTitle className="text-sm">Izaberite kategoriju</DrawerTitle>
          </DrawerHeader>

          <div className="space-y-2 p-4">
            {items.map((item) => {
              const isActive = item.id === selectedId;

              return (
                <Link
                  key={item.id}
                  href={item.href}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => onNavigate?.(item)}
                  className={`flex w-full items-center justify-between text-sm font-medium transition-colors ${
                    isActive
                      ? "text-primary underline underline-offset-4"
                      : "text-muted-foreground"
                  }`}
                >
                  <span>{item.label}</span>
                  {item.count !== undefined && (
                    <span className="border-border ml-2 flex h-6 min-w-6 shrink-0 items-center justify-center rounded-md border">
                      {item.count}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>
    </nav>
  );
}
